import { FocusEvent, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import { BasicFormFieldProps } from '../..';
import { NakedButton } from '../../../../naked';
import { Button } from '../../../button';
import { useCalendar } from '../../../calendar';
import { Text } from '../../../text';
import { useTooltip } from '../../../tooltip';
import { DateNavigatorInput } from '../../atoms/date-navigator-input';
import { FieldLayout } from '../../layouts';
import { DatePickerDialog } from '../date-picker/date-picker-dialog.component';
import { useDatePickerDialog } from '../date-picker/use-date-picker';
import { DateNavigatorWrapper } from './date-navigator-wrapper';
import { styles } from './date-navigator.styles';
import { parseSmartDate } from './helpers';

export type TrackingIds = {
  prevButton?: string;
  nextButton?: string;
  dateInput?: string;
  datePicker?: string;
};

type DateNavigator = {
  navigationButtonHoverLabel?: { next: string; prev: string };
  hideNavigationButtons?: boolean;
  trackingIds?: TrackingIds;
  selectorWidth?: number;
  inputPlaceholder?: string;
};

export type DateNavigatorProps = BasicFormFieldProps<'datePicker'> & DateNavigator;

export const DateNavigator = ({
  value,
  today,
  hideNavigationButtons,
  navigationButtonHoverLabel,
  trackingIds,
  blackoutDates,
  maxDate,
  minDate,
  selectorWidth = 134,
  inputPlaceholder,
  onBlur,
  onChange,
  onFocus,
  ...rest
}: DateNavigatorProps) => {
  const { t } = useTranslation();
  const [showInputField, setShowInputField] = useState(false);
  const [originalValue, setOriginalValue] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const { setOpen, Tooltip, triggerProps: tooltipTriggerProps, tooltipProps } = useTooltip({ hoverDelay: 1000 });

  const formattedDateObj = useMemo(() => {
    const dateObj = dayjs(value);
    return {
      day: dateObj.format('ddd'),
      date: dateObj.format('MMM D'),
      year: dateObj.format('YYYY'),
      isValid: value ? dateObj.isValid() : true,
    };
  }, [value]);

  const placeholder = useMemo(() => {
    const todayDateObj = dayjs();
    return inputPlaceholder ? inputPlaceholder : `"${todayDateObj.format('MMM D')}" or ${todayDateObj.format('M/D')}`;
  }, [inputPlaceholder]);

  const handleBlur = (event?: FocusEvent<HTMLInputElement>) => {
    if (event?.target.value !== undefined && event?.target.value !== '') {
      const formattedDate = parseSmartDate(event.target.value);
      onChange({ name: rest.name, value: formattedDate });
    } else if (event?.target.value === '') {
      onChange({ name: rest.name, value: originalValue || value });
    }

    onBlur?.(event);
    setShowInputField(false);
  };

  const {
    open: popoverActive,
    dialogProps,
    triggerProps,
    toggle,
    closeDialog,
    debouncedBlur,
    debouncedFocus,
  } = useDatePickerDialog({
    placement: 'bottom',
    trigger: triggerRef,
    clickOutsideIgnoreRefs: [triggerRef],
    offsetDistance: [8, 0],
    onBlur: handleBlur,
    onFocus,
  });

  const handleDateSelect = (selectedDate: string | string[]) => {
    const dateValue = Array.isArray(selectedDate) ? selectedDate[0] : selectedDate;
    onChange({ name: rest.name, value: dateValue });
    closeDialog();
  };

  const handleNavigation = (num: number) => {
    const newDate = dayjs(value).add(num, 'day').format('MM/DD/YYYY');
    onChange({ name: rest.name, value: newDate });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur(e);
    }
  };

  const calendarProps = useCalendar({
    value,
    dayNameFormat: 'one',
    today,
    maxDate,
    minDate,
    blackoutDates,
    onSelect: handleDateSelect,
  });

  const isActive = popoverActive || showInputField;
  const showError = !formattedDateObj.isValid && !showInputField; // Show error only when focus is out of input

  return (
    <>
      {/* Date Selector */}
      <DateNavigatorWrapper
        trackingIds={trackingIds}
        showNavigationButtons={!hideNavigationButtons}
        navigationButtonHoverLabel={navigationButtonHoverLabel}
        handleNavigation={handleNavigation}
      >
        <div css={[styles.dateSelectorContainer(isActive, showError), { width: selectorWidth }]}>
          <Button
            {...triggerProps}
            css={styles.calendarIcon}
            trackingId={trackingIds?.datePicker}
            variant={popoverActive ? 'tertiary' : 'secondary'}
            size='small'
            iconName='calendar'
            onClick={toggle}
          />
          {showInputField ? (
            <>
              <FieldLayout
                className='datepicker-input-field'
                fieldComponentProps={{ ref: inputFieldRef, autoFocus: true }}
                field={DateNavigatorInput as ComponentProps<typeof FieldLayout>['field']}
                hideErrorText
                placeholder={placeholder}
                value={value}
                onBlur={debouncedBlur}
                onFocus={debouncedFocus}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                {...rest}
              />
            </>
          ) : (
            <>
              <NakedButton
                {...tooltipTriggerProps}
                css={styles.dateLabel}
                trackingId={trackingIds?.dateInput}
                onClick={() => {
                  // Store the original value when showing the input field
                  setOriginalValue(value);
                  setShowInputField(true);
                  onChange({ name: rest.name, value: '' });
                  setOpen(false);
                }}
              >
                {formattedDateObj.isValid ? (
                  <div css={styles.dateText}>
                    <Text weight='regular'>{formattedDateObj.day}</Text>
                    <Text weight='bold'>{formattedDateObj.date}</Text>
                  </div>
                ) : (
                  <Text weight='regular'>{value}</Text>
                )}
              </NakedButton>
              {formattedDateObj.isValid && (
                <Tooltip {...tooltipProps}>
                  {t(`${formattedDateObj.day} ${formattedDateObj.date}, ${formattedDateObj.year}`)}
                </Tooltip>
              )}
            </>
          )}
        </div>
        {showError && (
          <Text size='small' color='error' css={{ position: 'absolute', zIndex: theme.zIndex.high }}>
            {t('Invalid date format')}
          </Text>
        )}
      </DateNavigatorWrapper>

      {/* Calendar Picker */}
      {/* @TODO: Replace `DatePickerDialog.Calendar` with new/existing component with latest design*/}
      <DatePickerDialog {...dialogProps}>
        <DatePickerDialog.Calendar {...calendarProps} />
      </DatePickerDialog>
    </>
  );
};
